'use client';

import { useEffect, useRef, type CSSProperties } from 'react';
import styles from './IdentityHeader.module.css';

/** Piso, en `rem`. El mismo valor que el `clamp` de `.fullName` en la hoja. */
const MIN_REM = 0.95;

/**
 * El nombre de la ficha: completo SIEMPRE, en una línea siempre que se pueda.
 *
 * A diferencia del cargo (`RoleLine`), esto NO se recorta ni se esconde detrás de
 * un globo, y no es una preferencia estética: es el `h1` de la página —protegido
 * por la regla de SEO de AGENTS.md— y además un nombre cortado es sencillamente
 * otro nombre. "Alejandra Andrea González Varga…" no informa: engaña.
 *
 * Así que en vez de recortar el texto se encoge la letra, y SÓLO lo necesario: un
 * nombre que entra cómodo se queda en su cuerpo pleno. Bajar el tamaño para todos
 * habría castigado al 66% que ya entra, para salvar al 12% que no.
 *
 * ── Quién hace qué, y por qué en ese orden ───────────────────────────────────
 *
 * El tamaño lo calcula el CSS, no este componente. La hoja estima el cuerpo a
 * partir del largo del nombre —que llega en `--chars`— y del ancho real de la
 * columna, así que el PRIMER PINTADO ya sale con el tamaño casi definitivo. Eso
 * es lo que evita el salto: antes el nombre se dibujaba a cuerpo pleno y sólo se
 * encogía al hidratar, un rato después.
 *
 * Este efecto queda como AJUSTE FINO, y sólo actúa si de verdad hace falta: la
 * estimación cuenta caracteres, y no todos miden igual —"Guillermo" ocupa más que
 * "Illimani" con las mismas nueve letras—. Cuando el texto real desborda, se
 * corrige con la medición del navegador; cuando ni al piso entra, se marca para
 * que caiga a dos líneas parejas. En la mayoría de los perfiles no hace nada.
 */
export function FullName({ text }: { text: string }) {
	const ref = useRef<HTMLHeadingElement>(null);

	useEffect(() => {
		const el = ref.current;
		if (!el) return;

		const ajustar = () => {
			// Limpiar el estilo en línea devuelve el mando al `clamp` de la hoja: sin
			// esto, cada pasada mediría sobre la corrección de la anterior y el nombre
			// se iría achicando solo.
			el.style.fontSize = '';
			el.dataset.dosLineas = 'false';

			const disponible = el.clientWidth;
			if (disponible === 0 || el.scrollWidth <= disponible) return; // el CSS acertó

			const actual = parseFloat(getComputedStyle(el).fontSize);
			const raiz = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
			// El 0.995 es holgura: `scrollWidth` viene redondeado a entero y sin ese pelo
			// el último carácter queda a un subpíxel de entrar.
			const corregido = actual * (disponible / el.scrollWidth) * 0.995;

			if (corregido >= MIN_REM * raiz) {
				el.style.fontSize = `${corregido.toFixed(2)}px`;
				return;
			}

			el.style.fontSize = `${MIN_REM}rem`;
			el.dataset.dosLineas = 'true';
		};

		ajustar();

		// Se observa el ANCHO del propio `h1`, que es `width: 100%` y por lo tanto no
		// cambia con el cuerpo de la letra: por eso ajustar adentro del callback no
		// vuelve a dispararlo. Observar el texto en sí sería un bucle.
		const observer = new ResizeObserver(ajustar);
		observer.observe(el);

		// Las tipografías web llegan después del primer render y con otras métricas:
		// sin esto, la única medición sería sobre la fuente de reserva.
		void document.fonts?.ready.then(ajustar);

		return () => observer.disconnect();
	}, [text]);

	return (
		<h1
			ref={ref}
			className={styles.fullName}
			data-dos-lineas="false"
			// El largo va al CSS para que pueda estimar el cuerpo antes de que corra
			// una línea de JS. Es lo único que el servidor sabe del texto sin medirlo.
			style={{ '--chars': text.length } as CSSProperties}
		>
			{text}
		</h1>
	);
}
